import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import moment from 'moment'
import {
  Axios,
  Grid,
  Segment,
  DataTable,
  Table,
  Checkbox,
  ConditionWrapper,
  Button,
  notice,
  OsrmMarker,
  BasicMap,
} from 'component/base'
import { Custom } from 'component/pieces'
import { useUserInfo } from 'component/hook'

export const GpsJunbanHaisha = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
  } = props

  const query = useMemo(
    () =>
      queryString.parse(window.location.search, {
        parseNumbers: true,
        parseBooleans: true,
      }),
    [window.location.search]
  )

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const { active_haiso_center_id } = useUserInfo()

  const defaultCondition = useMemo(() => {
    return {
      haiso_center_id : active_haiso_center_id
    }
  }, [ active_haiso_center_id ])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [refresh, setRefresh] = useState(0)
  const [enableAutoRefresh, setEnableAutoRefresh] = useState(true)
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )
  const [mode, setMode] = useState('list')

  const onSelectChange = (selectedIds, selectedRows) => {
    setSelectedIds(selectedIds)
    setSelectedRows(selectedRows)
  }

  const handleChangeMode = () =>
    mode === 'list'
      ? setMode('map')
      : mode === 'map'
      ? setMode('list')
      : setMode('')

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
        ? [selectedId]
        : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data.list, row =>
        _.find(selectedIds, id => row.gps_taiki_id === id)
      )
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
  }, [condition, refresh])

  useEffect(() => {
    const timer = setInterval(() => {
      if (!enableAutoRefresh) return
      setRefresh(refresh => ++refresh)
    }, 30000)

    return () => clearTimeout(timer)
  }, [enableAutoRefresh])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)

    const yokujitsuHaisha = condition.yokujitsu_haisha_kbn || [];

    const { data, error } = await Axios.ajax.get(
      '/api/staff/haisha/gps_junban_haisha',
      {
        params: {
          haiso_center_id: condition.haiso_center_id,
          gps_taiki_jotai_kbn: condition.gps_taiki_jotai_kbn,
          is_am: yokujitsuHaisha.includes("am") ? true : undefined,
          is_pm: yokujitsuHaisha.includes("pm") ? true : undefined,
        },
      }
    )
    setLoading(false)
    if (error) return
    const list = (data.taiki_result || [])
    .filter(item => item.gps_taiki_id)
    .map((item, index) => ({
      gps_taiki_id: item.gps_taiki_id,
      kumiaiin_id: item.kumiaiin_id,
      gps_haisha_kyohi_id: item.gps_haisha_kyohi_id,
      gps_haisha_kanryo_id: item.gps_haisha_kanryo_id,
      haiso_center_id: item.haiso_center_id,
      haiso_center_name: item.haiso_center_name,
      junban: item.junban,
      junban_display: item.junban_display,
      is_kurikoshi: item.is_kurikoshi,
      unsoten_name: item.unsoten_name,
      shashu_kbn: item.shashu_kbn,
      gps_taiki_jotai_kbn: item.gps_taiki_jotai_kbn,
      gps_taiki_jotai_kbn_name: item.gps_taiki_jotai_kbn_name,
      gps_taiki_datetime: item.gps_taiki_datetime,
      address: item.address,
      kusha_yotei_koshin_datetime: item.kusha_yotei_koshin_datetime,
      is_haisha_kyohi: item.is_haisha_kyohi,
      haisha_chui_jiko: item.haisha_chui_jiko,
      kusha_yotei_list: item.kusha_yotei_list || [],
      tsuiseki_point: item.tsuiseki_point,
      daihyo_tel: item.daihyo_tel,
      daihyo_mobile_tel: item.daihyo_mobile_tel,
    }))

    setData({
      list: list,
      is_gps_junban_haisha: data.is_gps_junban_haisha,
      kurikoshi_umu_kbn: data.kurikoshi_umu_kbn,
    })

    setSelectedIds(selectedIds =>
      _.intersection(selectedIds, _(list).map('gps_taiki_id').value())
    )
  }

  const haisha_kanryo = async gps_taiki_ids => {
    setLoading(true)
    const results = []

    for (const id of gps_taiki_ids) {
      const { error } = await Axios.ajax.post(
        `/api/staff/haisha/gps_haisha_kanryo`,
        { 
          gps_taiki_id: id 
        }
      )

      results.push(error ? null : true)
    }

    setLoading(false)

    if (results.some(r => r === null)) {
      notice({
        title: 'Error',
        description: '一部または全ての処理に失敗しました',
        type: 'error',
      })
      return
    }

    notice({
      title: 'Success',
      description: '処理が正常に終了しました',
      type: 'success',
    })

    setSelectedIds([])
    setRefresh(refresh => refresh + 1)
  }

  const haisha_kyohi_post = async kumiaiin_id => {
    setLoading(true)
    const { error } = await Axios.ajax.post(
      `/api/staff/kumiaiin/kumiaiin/${kumiaiin_id}/gps_haisha_kyohi`,
      {
        kumiaiin_id: kumiaiin_id
      }
    )
    setLoading(false)
    if (error) {
      notice({
        title: 'Error',
        description: '処理に失敗しました',
        type: 'error',
      })
      return
    }
    notice({
      title: 'Success',
      description: '処理が正常に終了しました',
      type: 'success',
    })

    setRefresh(refresh => ++refresh)
  }

  const haisha_kyohi_delete = async (kumiaiin_id, gps_haisha_kyohi_id) => {
    setLoading(true)
    const { error } = await Axios.ajax.delete(
      `/api/staff/kumiaiin/kumiaiin/${kumiaiin_id}/gps_junban_haisha/gps_haisha_kyohi/${gps_haisha_kyohi_id}`
    )
    setLoading(false)
    if (error) {
      notice({
        title: 'Error',
        description: '処理に失敗しました',
        type: 'error',
      })
      return
    }
    notice({
      title: 'Success',
      description: '処理が正常に終了しました',
      type: 'success',
    })

    setRefresh(refresh => ++refresh)
  }
  
  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const TableHeaderWithProps = useCallback(
    props => <TableHeader {...props}/>,
    []
  )

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
        haisha_kyohi_post={haisha_kyohi_post}
        haisha_kyohi_delete={haisha_kyohi_delete}
      />
    ),
    [history, handleChangeSelect]
  )

  const SearchElementWithProps = useCallback(
    props => <SearchElement {...props}/>,
    []
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          condition={condition}
          defaultCondition={defaultCondition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElementWithProps}
          enableSidebar={false}
          compact
          extraContent={
            <Grid>
              <Grid.Row>
                <>
                  <Grid.Column width={4}>
                    <Button
                      content='配車完了'
                      primary
                      onClick={() => haisha_kanryo(_.map(selectedRows, 'gps_taiki_id'))}
                      style={{ marginRight: '8px' }}
                      disabled={ selectedIds.length === 0 || selectedRows.some(row => row.gps_haisha_kanryo_id !== undefined || null) }
                    />
                    <Button
                      content={
                        mode === 'list'
                          ? 'ルートマップを表示する'
                          : mode === 'map'
                          ? '一覧を表示する'
                          : ''
                      }
                      onClick={() => handleChangeMode()}
                      disabled={ mode === 'list' ? selectedIds.length === 0 : false }
                    />
                  </Grid.Column>

                  <Grid.Column width={12} style={{ textAlign: 'right' }}>
                    <Button
                      content={enableAutoRefresh ? '自動更新ON' : '自動更新OFF'}
                      positive={enableAutoRefresh}
                      loading={enableAutoRefresh ? loading : false}
                      onClick={() => setEnableAutoRefresh(state => !state)}
                      style={{ marginRight: '8px' }}
                    />
                    <Button
                      content='更新'
                      onClick={() => setRefresh(state => ++state)}
                    />
                  </Grid.Column>
                </>
              </Grid.Row>
            </Grid>
          }
        >
          <DataTable
            name={`gps-junban-haisha`}
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={TableHeaderWithProps}
            tableRow={TableRowWithProps}
            data={data.list}
            bottomControl={<></>}
            selectedIds={selectedIds}
            style={{
              display: mode === 'map' ? 'none' : null,
            }}
          />
        </ConditionWrapper>

        {mode === 'map' && (
          <UnsotenMap
            targets={_(data.list)
              .filter(row => selectedRows.some(sel => sel.gps_taiki_id === row.gps_taiki_id))
              .orderBy(['gps_taiki_datetime'], ['desc'])
              .map(row => ({
                latLng: row.tsuiseki_point,
                iconUrl: '/images/akabokun.png',
                tooltip: <p>{row.unsoten_name}</p>,
              }))
              .value()}
          />
        )}
      </Segment>
    </>
  )
})

GpsJunbanHaisha.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
}

GpsJunbanHaisha.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => {},
}

const TableHeader = props => {
  const { withOrder, handleSort, column, direction } = props
  const criteria_date = moment().add(1, 'days')

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'haiso_center_name' ? direction : null}
        onClick={() => handleSort('haiso_center_name')}
        content='所属配送センター'
      />
      <Table.HeaderCell
        style={{ width: '5.0rem' }}
        sorted={column === 'junban_display' ? direction : null}
        onClick={() => handleSort('junban_display')}
        content='順番'
      />
      <Table.HeaderCell
        style={{ width: '5.0rem' }}
        sorted={column === 'is_kurikoshi' ? direction : null}
        onClick={() => handleSort('is_kurikoshi')}
        content='繰越'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'unsoten_name' ? direction : null}
        onClick={() => handleSort('unsoten_name')}
        content='運送店名'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'shashu_kbn' ? direction : null}
        onClick={() => handleSort('shashu_kbn')}
        content='車種'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'gps_taiki_jotai_kbn_name' ? direction : null}
        onClick={() => handleSort('gps_taiki_jotai_kbn_name')}
        content='状態'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'gps_taiki_datetime' ? direction : null}
        onClick={() => handleSort('gps_taiki_datetime')}
        content='待機登録'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'address' ? direction : null}
        onClick={() => handleSort('address')}
        content='最新位置'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === criteria_date.clone().format('YYYY/MM/DD') ? direction : null}
        onClick={() => handleSort(criteria_date.clone().format('YYYY/MM/DD'))}
        content={`${criteria_date.clone().format('MM/DD')}空車`}
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === criteria_date.clone().add(1, 'days').format('YYYY/MM/DD') ? direction : null}
        onClick={() => handleSort(criteria_date.clone().add(1, 'days').format('YYYY/MM/DD'))}
        content={`${criteria_date.clone().add(1, 'days').format('MM/DD')}空車`}
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === criteria_date.clone().add(2, 'days').format('YYYY/MM/DD') ? direction : null}
        onClick={() => handleSort(criteria_date.clone().add(2, 'days').format('YYYY/MM/DD'))}
        content={`${criteria_date.clone().add(2, 'days').format('MM/DD')}空車`}
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === criteria_date.clone().add(3, 'days').format('YYYY/MM/DD') ? direction : null}
        onClick={() => handleSort(criteria_date.clone().add(3, 'days').format('YYYY/MM/DD'))}
        content={`${criteria_date.clone().add(3, 'days').format('MM/DD')}空車`}
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === criteria_date.clone().add(4, 'days').format('YYYY/MM/DD') ? direction : null}
        onClick={() => handleSort(criteria_date.clone().add(4, 'days').format('YYYY/MM/DD'))}
        content={`${criteria_date.clone().add(4, 'days').format('MM/DD')}空車`}
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === criteria_date.clone().add(5, 'days').format('YYYY/MM/DD') ? direction : null}
        onClick={() => handleSort(criteria_date.clone().add(5, 'days').format('YYYY/MM/DD'))}
        content={`${criteria_date.clone().add(5, 'days').format('MM/DD')}空車`}
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === criteria_date.clone().add(6, 'days').format('YYYY/MM/DD') ? direction : null}
        onClick={() => handleSort(criteria_date.clone().add(6, 'days').format('YYYY/MM/DD'))}
        content={`${criteria_date.clone().add(6, 'days').format('MM/DD')}空車`}
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'kusha_yotei_koshin_datetime' ? direction : null}
        onClick={() => handleSort('kusha_yotei_koshin_datetime')}
        content='空車予定更新'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'is_haisha_kyohi' ? direction : null}
        onClick={() => handleSort('is_haisha_kyohi')}
        content='配車拒否'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'haisha_chui_jiko' ? direction : null}
        onClick={() => handleSort('haisha_chui_jiko')}
        content='配車注意事項'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'daihyo_tel' ? direction : null}
        onClick={() => handleSort('daihyo_tel')}
        content='代表電話番号'
      />
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'daihyo_mobile_tel' ? direction : null}
        onClick={() => handleSort('daihyo_mobile_tel')}
        content='代表携帯番号'
      />
    </Table.Row>
  )
}

const TableRow = props => {
  const { withOrder, data, selectedIds, handleChangeSelect, haisha_kyohi_post, haisha_kyohi_delete } = props || {}

  const {
    gps_taiki_id,
    kumiaiin_id,
    gps_haisha_kyohi_id,
    gps_haisha_kanryo_id,
    haiso_center_name,
    junban,
    is_kurikoshi,
    unsoten_name,
    shashu_kbn,
    gps_taiki_jotai_kbn,
    gps_taiki_jotai_kbn_name,
    address,
    gps_taiki_datetime,
    kusha_yotei_koshin_datetime,
    is_haisha_kyohi,
    haisha_chui_jiko,
    kusha_yotei_list = [],
    daihyo_tel,
    daihyo_mobile_tel,
  } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(gps_taiki_id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  const day0 = moment().add(1, 'days').format('YYYY/MM/DD')
  const day1 = moment().add(2, 'days').format('YYYY/MM/DD')
  const day2 = moment().add(3, 'days').format('YYYY/MM/DD')
  const day3 = moment().add(4, 'days').format('YYYY/MM/DD')
  const day4 = moment().add(5, 'days').format('YYYY/MM/DD')
  const day5 = moment().add(6, 'days').format('YYYY/MM/DD')
  const day6 = moment().add(7, 'days').format('YYYY/MM/DD')

  const getKusha = (date) => {
    const yotei = kusha_yotei_list.find(k => k.yotei_date === date)
    if (!yotei) return ''
    if (yotei.is_am && yotei.is_pm) return 'AM,PM'
    if (yotei.is_am) return 'AM'
    if (yotei.is_pm) return 'PM'
    return ''
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      style={{
        backgroundColor: 
          is_haisha_kyohi
          ? 'red'
          : gps_haisha_kanryo_id !== undefined || null
          ? 'orange'
          : undefined
      }}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(gps_taiki_id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={gps_taiki_id} />
        }
      />
      <Table.Cell style={{ width: '15.0rem' }} content={haiso_center_name} />
      <Table.Cell style={{ width: '5.0rem' }} content={junban || '-'} />
      <Table.Cell
        style={{ width: '5.0rem' }}
        content={is_kurikoshi ? '繰越' : '-'}
      />
      <Table.Cell style={{ width: '15.0rem' }} content={unsoten_name} />
      <Table.Cell style={{ width: '10.0rem' }} content={shashu_kbn} />
      <Table.Cell style={{ width: '10.0rem' }} content={gps_taiki_jotai_kbn_name} />
      <Table.Cell style={{ width: '15.0rem' }} content={gps_taiki_datetime} />
      <Table.Cell style={{ width: '15.0rem' }} content={address} />
      <Table.Cell style={{ width: '10.0rem' }} content={getKusha(day0)} />
      <Table.Cell style={{ width: '10.0rem' }} content={getKusha(day1)} />
      <Table.Cell style={{ width: '10.0rem' }} content={getKusha(day2)} />
      <Table.Cell style={{ width: '10.0rem' }} content={getKusha(day3)} />
      <Table.Cell style={{ width: '10.0rem' }} content={getKusha(day4)} />
      <Table.Cell style={{ width: '10.0rem' }} content={getKusha(day5)} />
      <Table.Cell style={{ width: '10.0rem' }} content={getKusha(day6)} />
      <Table.Cell style={{ width: '15.0rem' }} content={kusha_yotei_koshin_datetime} />
      <Table.Cell
        style={{ width: '10.0rem' }}
        content={
          is_haisha_kyohi ? (
            <Button
              content="拒否中"
              onClick={() => {
                haisha_kyohi_delete(kumiaiin_id, gps_haisha_kyohi_id)
              }}
            />
          ) : (
            <Button
              content="拒否にする"
              onClick={() => {
                haisha_kyohi_post(kumiaiin_id)
              }}
            />
          )
        }
      />
      <Table.Cell style={{ width: '15.0rem' }} content={haisha_chui_jiko} />
      <Table.Cell style={{ width: '15.0rem' }} content={daihyo_tel} />
      <Table.Cell style={{ width: '15.0rem' }} content={daihyo_mobile_tel} />
    </Table.Row>
  )
}

const SearchElement = props => {
  const { condition } = props

  return (
    <>
      <Grid.Column>
        <Custom.Dropdown.HaisoCenter
          value={condition.haiso_center_id}
          name='haiso_center_id'
          label='配車センター'
        />
      </Grid.Column>

      <Grid.Column>
        <Custom.Dropdown.GpsTaikiJokyoKbn
          value={condition.gps_taiki_jotai_kbn}
          name='gps_taiki_jotai_kbn'
          label='GPS待機状況'
          fluid
          multiple
        />
      </Grid.Column>

      <Grid.Column>
        <Custom.Dropdown.HaishaKiboKbn
          value={condition.yokujitsu_haisha_kbn}
          name="yokujitsu_haisha_kbn"
          label='翌日配車'
          fluid
          multiple
        />
      </Grid.Column>
    </>
  )
}

const UnsotenMap = props => {
  const {
    loading,
    targets,
    zoom,
  } = props

  const Markers = useMemo(() => {
    const arr = []

    targets.forEach((target, i) => {
      const { latLng, iconUrl, tooltip } = target || {}

      const [targetLat, targetLng] = latLng || []

      arr.push(
        <OsrmMarker
          key={i}
          latLng={[targetLat, targetLng]}
          iconUrl={iconUrl}
          tooltip={tooltip}
        />
      )
    })

    return arr
  }, [targets])

  const centerLat = targets[0]?.latLng?.[0]
  const centerLng = targets[0]?.latLng?.[1]

  return (
    <Segment loading={loading} basic style={{ padding: 0 }}>
      {!loading && (
        <BasicMap
          latitude={centerLat}
          longitude={centerLng}
          zoom={zoom}
          markers={Markers}
          loading={loading}
          nowOnMarker={false}
        />
      )}
    </Segment>
  )
}

UnsotenMap.propTypes = {
  base: PropTypes.object,
  targets: PropTypes.array,
  zoom: PropTypes.number,
}

UnsotenMap.defaultProps = {
  base: {
    latLng: [0, 0],
  },
  targets: [
    {
      iconUrl: '/images/akabokun.png',
      tooltip: <p>no name</p>,
      popup: <p>no name</p>,
      latLng: [0, 0],
    },
  ],
  zoom: 15,
}
